// IntegratedMenuPage.jsx — 통합 메뉴 진입점 + feature_type 디스패처 (Phase 1-1d)
// IntegratedMenuPlaceholder.jsx를 흡수: URL/Props 듀얼 모드 + feature_type별 분기.
//
// 진입 모드 2가지:
//   1. URL params: /admin/integrated/:menuId  (admin 사이드바)
//      → /api/my/integrated-menus 에서 메뉴 정보 fetch
//   2. Props: dashboard 탭
//      → menuIdProp/menuNameProp/featureTypeProp/companyCodesProp 직접 전달
//
// feature_type 분기:
//   - 'leave' → IntegratedLeaves 렌더 (실제 연차 화면)
//   - 그 외   → 준비 중 안내 메시지 (fallback)
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import IntegratedLeaves from './IntegratedLeaves';
// 출퇴근·출퇴근 달력 메뉴 = 본사처럼 [달력][직원별 상세] 탭 화면 한 벌 (2026-09-30, 옛 IntegratedAttendance·IntegratedAttendanceCalendar 대신)
import IntegratedAttendanceHub from './attendancehub/IntegratedAttendanceHub';
import IntegratedContractRenewal from './IntegratedContractRenewal';
import IntegratedMarketerConsultant from './IntegratedMarketerConsultant';
import IntegratedTmCalls from './IntegratedTmCalls'; // 통합 일일콜(2026-10-13, 통합 계획 3단계)
import IntegratedContracts from './IntegratedContracts'; // 통합 계약(2026-10-15, 통합 계획 4단계)
import IntegratedPromotional from './IntegratedPromotional'; // 통합 홍보물(2026-10-19, 통합 계획 5단계)
import AdminMeetingMap from './AdminMeetingMap'; // 통합 미팅지도(2026-10-22)
import { meetingMapApi } from '../tm/meetingMapApi';
import RegionCoverage from './regioncoverage/RegionCoverage'; // 통합 방문빈도 관리(2026-10-29)
// 페이지 내부 고정 제목 (feature_type 기반) — 사이드바의 메뉴 이름(자유 입력)과 구분. 이름표는 한 곳(2026-10-01)
import { integratedFeatureLabel } from './integratedmenu/integratedFeatureMeta';

export default function IntegratedMenuPage({ menuIdProp, menuNameProp, featureTypeProp, companyCodesProp }) {
  const params = useParams();
  const fromProps = menuIdProp !== undefined;
  const menuId = fromProps ? menuIdProp : params.menuId;

  const [menu, setMenu] = useState(
    fromProps
      ? { id: menuIdProp, name: menuNameProp, feature_type: featureTypeProp, company_codes: companyCodesProp }
      : null
  );
  const [loading, setLoading] = useState(!fromProps);
  const [notFound, setNotFound] = useState(false);
  // 내 통합 메뉴 목록 전체 — 출퇴근 달력 메뉴가 같은 회사 묶음의 출퇴근 메뉴(짝)를 찾는 데 쓴다. Props 모드는 모름(짝 없음).
  const [myMenus, setMyMenus] = useState([]);

  // URL 모드: 본인이 접근 가능한 통합 메뉴 목록에서 menuId 매칭
  useEffect(() => {
    if (fromProps) return;
    apiClient.get('/api/my/integrated-menus')
      .then((res) => {
        const list = (res.ok && Array.isArray(res.data?.list)) ? res.data.list : [];
        const found = list.find((m) => Number(m.id) === Number(menuId));
        setMyMenus(list);
        if (found) setMenu(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [menuId, fromProps]);

  if (loading) return <div style={pageStyle}><div style={cardStyle}>로딩 중...</div></div>;

  if (notFound) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h2 style={titleStyle}>접근 권한 없음</h2>
        <p style={textStyle}>이 통합 메뉴(id: {menuId})에 접근할 권한이 없거나 메뉴가 존재하지 않습니다.</p>
      </div>
    </div>
  );

  // ── feature_type 분기: 연차/출퇴근은 실제 화면, 그 외는 준비 중 메시지 ────
  // pageTitle은 feature_type 기반 고정 제목. 메뉴 이름(menu.name)은 사이드바/관리 페이지에서만 사용.
  if (menu.feature_type === 'leave') {
    return (
      <IntegratedLeaves
        menuId={menu.id}
        companyCodes={menu.company_codes || []}
        pageTitle={integratedFeatureLabel(menu.feature_type)}
      />
    );
  }
  if (menu.feature_type === 'attendance' || menu.feature_type === 'calendar') {
    // 2026-09-30: 출퇴근 메뉴 = [달력][직원별 상세] 탭, 달력 메뉴 = [달력]만(짝 출퇴근 메뉴가 있으면 그 달력 탭으로 넘김).
    //   제목은 탭 화면이 정한다(출퇴근 관리 / 달력 메뉴는 출퇴근 달력).
    return <IntegratedAttendanceHub menu={menu} menus={myMenus} />;
  }
  if (menu.feature_type === 'contract_renewal') {
    return (
      <IntegratedContractRenewal
        menuId={menu.id}
        companyCodes={menu.company_codes || []}
        pageTitle={integratedFeatureLabel(menu.feature_type)}
      />
    );
  }
  if (menu.feature_type === 'marketer_consultant') {
    return (
      <IntegratedMarketerConsultant
        menuId={menu.id}
        companyCodes={menu.company_codes || []}
        pageTitle={integratedFeatureLabel(menu.feature_type)}
      />
    );
  }

  if (menu.feature_type === 'tm_call') {
    return (
      <IntegratedTmCalls
        menuId={menu.id}
        companyCodes={menu.company_codes || []}
        pageTitle={integratedFeatureLabel(menu.feature_type)}
      />
    );
  }

  if (menu.feature_type === 'contract') {
    // 제목은 계약 관리 화면이 그린다('계약 관리' = 이 기능 이름표와 같다)
    return <IntegratedContracts menuId={menu.id} companyCodes={menu.company_codes || []} />;
  }

  if (menu.feature_type === 'promotional') {
    // 제목은 홍보물 관리 화면이 그린다('홍보물 관리' = 이 기능 이름표와 같다)
    return <IntegratedPromotional menuId={menu.id} />;
  }

  if (menu.feature_type === 'meeting_map') {
    // 미팅지도 관리 화면 한 벌 — 주소만 이 메뉴 것. 설정(⚙️)은 테넌트 공용이라 이 메뉴 열쇠로도 바꾼다(사용자 결정).
    return (
      <AdminMeetingMap
        key={menu.id}
        api={meetingMapApi(`/api/admin/integrated-menus/${menu.id}/meeting-map`)}
        settingsKeys={[`INTEGRATED_MENU_${menu.id}`, 'MEETING_MAP_MANAGEMENT']}
        subtitle="메뉴에 묶은 회사들의 미팅을 한 지도에서 관리합니다."
      />
    );
  }

  if (menu.feature_type === 'region_coverage') {
    // 방문빈도 관리 화면 한 벌 — 주소만 이 메뉴 것. 메뉴 회사들의 방문을 분류로 합쳐 본다. 색상 기준(⚙️)은 메뉴 테넌트 것이라 이 메뉴 열쇠로도 바꾼다.
    return (
      <RegionCoverage
        key={menu.id}
        adminMode
        api={{
          coverageUrl: `/api/admin/integrated-menus/${menu.id}/region-coverage`,
          rulesUrl: `/api/admin/integrated-menus/${menu.id}/region-color-rules`,
          manageKeys: [`INTEGRATED_MENU_${menu.id}`, 'REGION_COVERAGE_MANAGEMENT'],
        }}
      />
    );
  }

  // Fallback: 미구현 feature_type
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={badgeStyle}>준비 중</div>
        <h2 style={titleStyle}>{menu.name}</h2>
        <div style={metaStyle}>
          <span style={metaItemStyle}>기능: {integratedFeatureLabel(menu.feature_type)}</span>
          <span style={metaItemStyle}>회사: {(menu.company_codes || []).join(', ')}</span>
        </div>
        <p style={textStyle}>이 통합 메뉴의 화면은 아직 구현되지 않았습니다.</p>
        <p style={hintStyle}>
          관리자: 업무/기능 배정 페이지에서 이 권한을 부여/회수할 수 있습니다.
        </p>
      </div>
    </div>
  );
}

const pageStyle    = { padding: 24, maxWidth: 720, margin: '0 auto' };
const cardStyle    = { background: 'var(--bg-card, #fff)', border: '1px solid var(--border, #eee)', borderRadius: 12, padding: 32, color: 'var(--text, #111)' };
const badgeStyle   = { display: 'inline-block', padding: '4px 12px', fontSize: 11, fontWeight: 700, borderRadius: 12, background: '#fef3c7', color: '#92400e', marginBottom: 12 };
const titleStyle   = { margin: '0 0 12px', fontSize: 22, fontWeight: 700 };
const metaStyle    = { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border, #eee)' };
const metaItemStyle = { fontSize: 13, color: 'var(--text-muted, #666)' };
const textStyle    = { fontSize: 14, lineHeight: 1.6, color: 'var(--text, #333)', margin: '8px 0' };
const hintStyle    = { fontSize: 12, color: 'var(--text-muted, #888)', marginTop: 20, padding: 12, background: 'var(--bg-subtle, #f8fafc)', borderRadius: 6 };
